import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Stepper } from "@/components/ui/stepper";
import { toast } from "@/components/ui/toast";
import { usePermission } from "@/hooks/use-permission";
import { createAdminInvite, fetchAdminGroups } from "@/lib/api";

const STEPS = [
  { label: "Profile" },
  { label: "Role & Groups" },
  { label: "Review & Send" },
];

const INVITE_ROLES = [
  { value: "organization_admin", label: "Organization Admin" },
  { value: "organization_admin_starter", label: "Organization Admin Starter" },
];

const EXPIRY_OPTIONS = ["24 hours", "48 hours", "72 hours"];

interface InviteGroup {
  id: string;
  name: string;
  description: string;
}

interface InviteFormData {
  fullName: string;
  email: string;
  role: string;
  groups: string[];
  inviteExpiry: string;
}

const INITIAL_FORM: InviteFormData = {
  fullName: "",
  email: "",
  role: INVITE_ROLES[0].value,
  groups: [],
  inviteExpiry: "48 hours",
};

const FALLBACK_GROUPS: InviteGroup[] = [];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-ink">{title}</h4>
      {children}
    </div>
  );
}

function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-ink">
      {children}
    </label>
  );
}

function InputBox(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)] disabled:opacity-60"
    />
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line py-2.5 last:border-b-0">
      <span className="text-xs font-semibold text-muted">{label}</span>
      <span className="text-right text-sm text-ink">{value}</span>
    </div>
  );
}

export default function InviteUserPage() {
  const navigate = useNavigate();
  const canInvite = usePermission("user:invite");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<InviteFormData>(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<InviteGroup[]>(FALLBACK_GROUPS);

  useEffect(() => {
    let cancelled = false;
    fetchAdminGroups().then((result) => {
      if (cancelled) return;
      if (!result.error && result.groups.length > 0) {
        setGroups(result.groups.map((g) => ({ id: g.groupId, name: g.name, description: g.description })));
      }
    });
    return () => { cancelled = true; };
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canNext =
    step === 0
      ? form.fullName.trim().length > 0 && form.email.trim().length > 0 && emailValid
      : true;

  function handleNext() {
    if (step < 2) setStep(step + 1);
  }

  function handleBack() {
    if (step > 0) setStep(step - 1);
  }

  function toggleGroup(name: string) {
    setForm((f) => ({
      ...f,
      groups: f.groups.includes(name) ? f.groups.filter((x) => x !== name) : [...f.groups, name],
    }));
  }

  async function handleSend() {
    setSending(true);
    setError(null);
    const expiryHours = form.inviteExpiry === "24 hours" ? 24 : form.inviteExpiry === "72 hours" ? 72 : 48;
    const result = await createAdminInvite({
      email: form.email,
      displayName: form.fullName,
      roleName: form.role,
      expiresInHours: expiryHours,
    });
    if (result.ok) {
      toast({ variant: "success", title: `Invitation sent to ${form.email}` });
      navigate("/admin/users");
    } else {
      setError(result.error ?? "Failed to send invite");
      setSending(false);
    }
  }

  const roleLabel = INVITE_ROLES.find((r) => r.value === form.role)?.label || form.role;

  return (
    <div className="mx-auto max-w-[720px]">
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <a href="#" onClick={(e) => e.preventDefault()} className="text-primary no-underline hover:underline">Identity</a>
        <span className="text-muted" aria-hidden>›</span>
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Users</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Invite</span>
      </nav>

      <div className="mb-6 flex items-center gap-3">
        <div className="grid size-10 place-items-center rounded-lg bg-primary-soft">
          <UserPlus className="size-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold text-ink">Invite User</h1>
          <p className="text-sm text-muted">Send a secure invite to a new team member.</p>
        </div>
      </div>

      <Stepper steps={STEPS} current={step} className="mb-8" />

      <Card>
        <CardBody>
          {step === 0 && (
            <div className="space-y-5">
              <Section title="Basic Information">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="invite-name">Full Name</Label>
                    <InputBox
                      id="invite-name"
                      value={form.fullName}
                      onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                      placeholder="e.g. Jordan Lee"
                    />
                  </div>
                  <div>
                    <Label htmlFor="invite-email">Email Address</Label>
                    <InputBox
                      id="invite-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="jordan@harbortrust.com"
                    />
                  </div>
                </div>
              </Section>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <Section title="Role">
                <div>
                  <Label htmlFor="invite-role">Assigned Role</Label>
                  <Select
                    id="invite-role"
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink"
                  >
                    {INVITE_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-muted">Additional roles can be assigned after the user accepts the invite.</p>
                </div>
                <div className="sm:max-w-[240px]">
                  <Label htmlFor="invite-expiry">Invite Expiry</Label>
                  <Select
                    id="invite-expiry"
                    value={form.inviteExpiry}
                    onChange={(e) => setForm((f) => ({ ...f, inviteExpiry: e.target.value }))}
                    className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink"
                  >
                    {EXPIRY_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </Select>
                </div>
              </Section>
              <Section title="Groups">
                <div className="grid gap-2 sm:grid-cols-2">
                  {groups.map((g) => (
                    <label
                      key={g.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-[8px] border px-3 py-2.5 transition-colors ${form.groups.includes(g.name) ? "border-primary bg-primary-soft" : "border-line bg-surface hover:border-line-strong"}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 accent-primary"
                        checked={form.groups.includes(g.name)}
                        onChange={() => toggleGroup(g.name)}
                      />
                      <div>
                        <div className="text-sm font-semibold text-ink">{g.name}</div>
                        <div className="text-[11px] text-muted">{g.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted">Group selections are not part of the invite request. Assign groups after the user accepts.</p>
              </Section>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <Section title="Review Invitation">
                <div className="rounded-[8px] border border-line bg-bg px-4">
                  <ReviewRow label="Full Name" value={form.fullName || "—"} />
                  <ReviewRow label="Email" value={form.email || "—"} />
                  <ReviewRow label="Role" value={roleLabel} />
                  <ReviewRow label="Groups" value={form.groups.length > 0 ? form.groups.join(", ") : "No groups selected"} />
                  <ReviewRow label="Invite Expiry" value={form.inviteExpiry} />
                </div>
              </Section>
            </div>
          )}
        </CardBody>
      </Card>

      {error && (
        <div className="mt-3 rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <Button variant="secondary" disabled={step === 0} onClick={handleBack} className="gap-1">
          <ChevronLeft className="size-4" /> Back
        </Button>
        {step < 2 ? (
          <Button disabled={!canNext} onClick={handleNext} className="gap-1">
            Next <ChevronRight className="size-4" />
          </Button>
        ) : (
          <Button onClick={handleSend} disabled={!canInvite} loading={sending}>Send Invite</Button>
        )}
      </div>
    </div>
  );
}
